/** Auth API calls (login / logout / me) + session persistence. */
import { apiFetch, clearAuth, readAuth, writeAuth } from './apiClient';

/**
 * Create an account. The API signs the new user in as part of the same call,
 * so this returns a session exactly like loginRequest does and the caller
 * cannot end up with an account but no session.
 */
export async function registerRequest(name, email, password) {
  const data = await apiFetch('/api/auth/register', {
    method: 'POST',
    auth: false,
    body: { name, email, password },
  });
  const session = {
    user: data.user,
    accessToken: data.accessToken,
    refreshToken: data.refreshToken,
  };
  writeAuth(session);
  return session;
}

export async function loginRequest(email, password) {
  const data = await apiFetch('/api/auth/login', {
    method: 'POST',
    auth: false,
    body: { email, password },
  });
  const session = {
    user: data.user,
    accessToken: data.accessToken,
    refreshToken: data.refreshToken,
  };
  writeAuth(session);
  return session;
}

export async function logoutRequest() {
  const auth = readAuth();
  try {
    if (auth?.refreshToken) {
      await apiFetch('/api/auth/logout', {
        method: 'POST',
        auth: false,
        body: { refreshToken: auth.refreshToken },
      });
    }
  } catch {
    /* best-effort server-side revoke */
  }
  clearAuth();
}

export async function meRequest() {
  return apiFetch('/api/auth/me');
}


// Password flows reached from an emailed link. All three are unauthenticated:
// the single-use token in the link is the credential.
export const requestPasswordReset = (email) =>
  apiFetch('/api/auth/password-reset/request', { method: 'POST', body: { email }, auth: false });
export const confirmPasswordReset = (token, password) =>
  apiFetch('/api/auth/password-reset/confirm', { method: 'POST', body: { token, password }, auth: false });
export const setPasswordFromInvite = (token, password) =>
  apiFetch('/api/auth/set-password', { method: 'POST', body: { token, password }, auth: false });


// Email confirmation. The link carries the credential, so confirming needs
// no session; resending does, since it goes to the address on file.
export const verifyEmail = (token) =>
  apiFetch('/api/auth/verify-email', { method: 'POST', body: { token }, auth: false });
export const resendVerification = () =>
  apiFetch('/api/auth/verify-email/resend', { method: 'POST' });

// Account self-service. Changing a password needs the current one even
// though the session is already valid: holding a session is not proof of
// being the account owner, only of having been near their screen.
export const updateProfile = (name) =>
  apiFetch('/api/auth/me', { method: 'PUT', body: { name } });
/**
 * Correct the address on an account that has never been confirmed. The server
 * refuses once it has been, because from that point the address is an identity
 * and moving it needs a confirmation round trip we have not built.
 */
export const correctEmail = (newEmail, currentPassword) =>
  apiFetch('/api/auth/me/email', { method: 'POST', body: { newEmail, currentPassword } });

export const changePassword = (currentPassword, newPassword) =>
  apiFetch('/api/auth/me/password', {
    method: 'POST',
    body: { currentPassword, newPassword },
  });
